import type { Camera } from "three";
import { Vector3 } from "three";
import { MeasurementType } from "@speckle/shared/viewer/state";

// No value-level @speckle/viewer import here — that pulls in the SDK's whole runtime, which a unit
// test of this pure geometry shouldn't have to load (see VertexDragExtension.test.ts).
export type Point3 = readonly [number, number, number];

export interface VertexRef {
  measurementId: string;
  index: number;
}

// The minimal shape collectSnapPoints needs from a measurement — reimplemented rather than imported
// from MeasurementData, so this pure-geometry module still doesn't have to load the SDK.
export interface SnapMeasurementLike {
  measurementId: string;
  type: MeasurementType;
  innerPoints?: Point3[];
  startPoint: Point3;
  endPoint: Point3;
}

export const pointsEqual = (a: Point3, b: Point3) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2];

// Chained lines copy the previous end point verbatim, but tolerate float noise from a rebuild's own
// re-derivation (same tolerance as measurementChain.ts's samePoint).
const POINT_TOLERANCE = 1e-6;
export const pointsClose = (a: Point3, b: Point3, tolerance: number = POINT_TOLERANCE): boolean =>
  Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]) <= tolerance;

export const worldToScreen = (
  point: Point3,
  camera: Camera,
  rect: { left: number; top: number; width: number; height: number }
) => {
  const projected = new Vector3(point[0], point[1], point[2]).project(camera);
  return {
    x: rect.left + ((projected.x + 1) / 2) * rect.width,
    y: rect.top + ((1 - projected.y) / 2) * rect.height,
  };
};

// A point behind the camera still projects to a (mirrored) screen position, so it must be filtered out first.
export const isInFrontOfCamera = (point: Point3, camera: Camera) => {
  const depth = new Vector3(point[0], point[1], point[2]).project(camera).z;
  return depth >= -1 && depth <= 1;
};

const distanceToPlane = (point: Point3, origin: Point3, normal: Point3) =>
  (point[0] - origin[0]) * normal[0] + (point[1] - origin[1]) * normal[1] + (point[2] - origin[2]) * normal[2];

// Newell's method: stable for any simple polygon, and exact when the corners are coplanar.
const newellVector = (points: Point3[]): Point3 => {
  let nx = 0;
  let ny = 0;
  let nz = 0;
  for (let i = 0; i < points.length; i++) {
    const [x1, y1, z1] = points[i];
    const [x2, y2, z2] = points[(i + 1) % points.length];
    nx += (y1 - y2) * (z1 + z2);
    ny += (z1 - z2) * (x1 + x2);
    nz += (x1 - x2) * (y1 + y2);
  }
  return [nx, ny, nz];
};

const withoutClosingPoint = (points: Point3[]) =>
  points.length > 1 && pointsEqual(points[0], points[points.length - 1]) ? points.slice(0, -1) : points;

// A planar polygon's Newell vector is twice its area, whatever plane it lies in.
export const polygonArea = (points: Point3[]): number => {
  const corners = withoutClosingPoint(points);
  if (corners.length < 3) return 0;
  return Math.hypot(...newellVector(corners)) / 2;
};

export const fitPlane = (points: Point3[]): { origin: Point3; normal: Point3 } | null => {
  if (points.length < 3) return null;
  const [nx, ny, nz] = newellVector(points);
  const length = Math.hypot(nx, ny, nz);
  if (length < 1e-12) return null;
  const sum = points.reduce<[number, number, number]>((acc, p) => [acc[0] + p[0], acc[1] + p[1], acc[2] + p[2]], [0, 0, 0]);
  return {
    origin: [sum[0] / points.length, sum[1] / points.length, sum[2] / points.length],
    normal: [nx / length, ny / length, nz / length],
  };
};

const PLANE_TOLERANCE = 1e-4;

// The SDK takes an area's plane from the first click's surface normal and flattens only the fill onto it,
// so a slightly tilted first normal left the corners and outline off the fill. Returns null when already aligned.
export const alignAreaToPlane = (
  innerPoints: Point3[],
  startPoint: Point3,
  startNormal: Point3
): { innerPoints: Point3[]; startPoint: Point3; startNormal: Point3 } | null => {
  const closed = innerPoints.length > 1 && pointsEqual(innerPoints[0], innerPoints[innerPoints.length - 1]);
  const corners = closed ? innerPoints.slice(0, -1) : innerPoints;
  const fit = fitPlane(corners);
  if (!fit) return null;
  const currentLength = Math.hypot(...startNormal);
  const current: Point3 =
    currentLength > 0
      ? [startNormal[0] / currentLength, startNormal[1] / currentLength, startNormal[2] / currentLength]
      : fit.normal;
  if (innerPoints.every((point) => Math.abs(distanceToPlane(point, startPoint, current)) <= PLANE_TOLERANCE)) return null;

  const flip = fit.normal[0] * current[0] + fit.normal[1] * current[1] + fit.normal[2] * current[2] < 0 ? -1 : 1;
  const normal: Point3 = [fit.normal[0] * flip, fit.normal[1] * flip, fit.normal[2] * flip];
  const flattened = corners.map((point): Point3 => {
    const distance = distanceToPlane(point, fit.origin, normal);
    return [point[0] - normal[0] * distance, point[1] - normal[1] * distance, point[2] - normal[2] * distance];
  });
  return { innerPoints: closed ? [...flattened, flattened[0]] : flattened, startPoint: flattened[0], startNormal: normal };
};

// Every point of every OTHER complete measurement, offered as a snap target for a dragged corner. The
// dragged shape's own corners are left out: snapping onto a neighbour would collapse the polygon.
const SAME_SURFACE_COS = Math.cos((15 * Math.PI) / 180);
// Metres off the plane, plus a share of the distance from the first point, so a roof's fall still counts as its top.
const OFF_PLANE_BASE = 0.05;
const OFF_PLANE_SLOPE = 0.05;

const dot = (a: Point3, b: Point3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const unit = (vector: Point3): Point3 | null => {
  const length = Math.hypot(...vector);
  return length > 1e-12 ? [vector[0] / length, vector[1] / length, vector[2] / length] : null;
};

const nearAreaPlane = (point: Point3, planeOrigin: Point3, normal: Point3): boolean => {
  const offset = distanceToPlane(point, planeOrigin, normal);
  const inPlane: Point3 = [
    point[0] - planeOrigin[0] - normal[0] * offset,
    point[1] - planeOrigin[1] - normal[1] * offset,
    point[2] - planeOrigin[2] - normal[2] * offset,
  ];
  return Math.abs(offset) <= OFF_PLANE_BASE + OFF_PLANE_SLOPE * Math.hypot(...inPlane);
};

// A corner vertex belongs to its side faces too, so one reached from the side still counts if it sits on the surface.
export const isOnAreaSurface = (point: Point3, planeOrigin: Point3, planeNormal: Point3): boolean => {
  const normal = unit(planeNormal);
  return !!normal && nearAreaPlane(point, planeOrigin, normal);
};

// While an area is drawn, a hover hit on another surface (the side face below an edge) moves to where the
// cursor ray meets the first point's plane; a hit on the same surface is kept, so its vertex snap still works.
export const constrainToAreaPlane = (input: {
  hitPoint: Point3;
  hitNormal: Point3;
  rayOrigin: Point3;
  rayDirection: Point3;
  planeOrigin: Point3;
  planeNormal: Point3;
}): { point: Point3; normal: Point3; constrained: boolean } => {
  const unchanged = { point: input.hitPoint, normal: input.hitNormal, constrained: false };
  const normal = unit(input.planeNormal);
  if (!normal) return unchanged;

  const hitNormal = unit(input.hitNormal);
  const sameFacing = !!hitNormal && Math.abs(dot(hitNormal, normal)) >= SAME_SURFACE_COS;
  if (sameFacing && nearAreaPlane(input.hitPoint, input.planeOrigin, normal)) return unchanged;

  const denominator = dot(input.rayDirection, normal);
  if (Math.abs(denominator) < 1e-9) return unchanged;
  const toPlane: Point3 = [
    input.planeOrigin[0] - input.rayOrigin[0],
    input.planeOrigin[1] - input.rayOrigin[1],
    input.planeOrigin[2] - input.rayOrigin[2],
  ];
  const distance = dot(toPlane, normal) / denominator;
  if (distance < 0) return unchanged;
  const point: Point3 = [
    input.rayOrigin[0] + input.rayDirection[0] * distance,
    input.rayOrigin[1] + input.rayDirection[1] * distance,
    input.rayOrigin[2] + input.rayDirection[2] * distance,
  ];
  return { point, normal, constrained: true };
};

export const collectSnapPoints = (measurements: SnapMeasurementLike[], exclude: VertexRef): Point3[] => {
  const points: Point3[] = [];
  for (const measurement of measurements) {
    if (measurement.measurementId === exclude.measurementId) continue;
    if (measurement.type === MeasurementType.AREA) {
      const innerPoints = measurement.innerPoints ?? [];
      const closed = innerPoints.length > 1 && pointsEqual(innerPoints[0], innerPoints[innerPoints.length - 1]);
      const vertexCount = closed ? innerPoints.length - 1 : innerPoints.length;
      for (let index = 0; index < vertexCount; index++) points.push(innerPoints[index]);
    } else if (measurement.type === MeasurementType.POINTTOPOINT) {
      points.push(measurement.startPoint, measurement.endPoint);
    }
  }
  return points;
};

// The minimal shape linesSharingPoint needs from a point-to-point line.
export interface LineVertexLike {
  measurementId: string;
  startPoint: Point3;
  endPoint: Point3;
}

export interface LineEndpointRef {
  measurementId: string;
  index: 0 | 1;
}

// Every OTHER line whose start or end sits at `point` — a chain junction, or a closed loop's closing
// point. A line can match on both ends at once (a degenerate zero-length line); both are reported.
export const linesSharingPoint = (lines: LineVertexLike[], point: Point3, excludeId: string): LineEndpointRef[] => {
  const result: LineEndpointRef[] = [];
  for (const line of lines) {
    if (line.measurementId === excludeId) continue;
    if (pointsClose(line.startPoint, point)) result.push({ measurementId: line.measurementId, index: 0 });
    if (pointsClose(line.endPoint, point)) result.push({ measurementId: line.measurementId, index: 1 });
  }
  return result;
};

// Coincident points tie on screen and the first candidate wins, so the panel's selection goes first, then
// the active kind: in Distance mode a shared point grabs the line, in Area mode the area. Sort is stable.
export const rankCandidates = <K, T extends { measurementId: string; kind: K }>(
  candidates: T[],
  priority: { selectedId: string | null; kind: K }
): T[] => {
  const rank = (candidate: T) => (candidate.measurementId === priority.selectedId ? 0 : candidate.kind === priority.kind ? 1 : 2);
  return [...candidates].sort((first, second) => rank(first) - rank(second));
};

// Pure so the nearest-point search is unit-testable without a real camera, DOM, or Extension instance.
// Generic over anything with a screen position — vertex-grab candidates and plain snap points alike.
export const findNearestScreenPoint = <T extends { x: number; y: number }>(
  candidates: T[],
  clientX: number,
  clientY: number,
  radiusPx: number
): T | null => {
  let closest: { ref: T; distSq: number } | null = null;
  for (const candidate of candidates) {
    const dx = candidate.x - clientX;
    const dy = candidate.y - clientY;
    const distSq = dx * dx + dy * dy;
    if (distSq <= radiusPx * radiusPx && (!closest || distSq < closest.distSq)) closest = { ref: candidate, distSq };
  }
  return closest?.ref ?? null;
};
